import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react'
import { OpenAIService } from '../services/openai'
import { ConversationService } from '../services/conversations'
import type {
  ChatMessage,
  ChatInterfaceProps,
  ChatInterfaceRef,
  ChatState,
  ConversationContext
} from '../types/chat'
import type { ChatPromptWithVideo } from '../services/chatPrompts'
import { DEFAULT_VIDEO_INITIAL_MESSAGE } from '../constants/chat'
import ChatPanel from './ChatPanel'

interface ChatInterfaceState extends ChatState {
  processedMessageIds: Set<string>
}

const ChatInterface = forwardRef<ChatInterfaceRef, ChatInterfaceProps>(({
  context,
  currentUser,
  className = '',
  height = 'h-[calc(100vh-20rem)]',
  onConversationChange,
  onConversationDeleted,
  showResetButton = false,
  forceNewConversation = false,
  conversationId,
  initialMessage
}, ref) => {
  const [state, setState] = useState<ChatInterfaceState>({
    messages: [],
    isAiTyping: false,
    isSending: false,
    newMessage: '',
    conversation: null,
    error: null,
    processedMessageIds: new Set<string>(),
    abortController: null
  })

  const hasSentInitial = useRef(false)

  // Auto-send initialMessage once the chat has rendered its welcome message
  useEffect(() => {
    if (!initialMessage || hasSentInitial.current) return
    if (!context.chatPrompt || state.isSending || state.messages.length === 0) return
    hasSentInitial.current = true
    handleSendMessage(initialMessage)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage, context.chatPrompt, state.isSending, state.messages.length])

  const getInitialMessage = (context: ConversationContext): string => {
    const promptWithInitial = context.chatPrompt as ChatPromptWithVideo & { initial_message?: string | null }

    if (promptWithInitial?.initial_message) {
      return promptWithInitial.initial_message
    }

    switch (context.type) {
      case 'video':
        return DEFAULT_VIDEO_INITIAL_MESSAGE
      case 'generic':
        return `こんにちは！MiraPASS総合アシスタントです🌟\n\n起業やビジネスについて何でもお気軽にご相談ください！\n\n例えば：\n💡 アイデアの見つけ方\n📊 市場調査の方法\n💰 資金調達について\n👥 チームづくりのコツ\n📈 マーケティング戦略\n\nどんな小さな疑問でも大歓迎です。一緒に素晴らしいアイデアを形にしていきましょう！`
      default:
        return 'こんにちは！何かご質問はありますか？'
    }
  }

  const loadExistingConversation = useCallback(async () => {
    if (!context.chatPrompt || !currentUser) return

    if (forceNewConversation) {
      setState(prev => {
        if (prev.messages.length > 0) return prev
        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          sender: 'ai',
          message: getInitialMessage(context),
          timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
        }
        return { ...prev, messages: [welcomeMessage] }
      })
      return
    }

    try {
      const { conversation, messages } = conversationId
        ? await ConversationService.getConversationWithMessages(conversationId)
        : await ConversationService.getUserConversationWithMessages(currentUser.id, context.chatPrompt.id)

      if (conversation && messages.length > 0) {
        setState(prev => ({ ...prev, conversation }))
        if (onConversationChange) {
          onConversationChange(conversation)
        }

        const formattedMessages: ChatMessage[] = messages.map(msg => ({
          id: msg.id,
          sender: msg.role === 'assistant' ? 'ai' : 'user',
          message: msg.content,
          timestamp: new Date(msg.created_at).toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit'
          })
        }))
        setState(prev => ({ ...prev, messages: formattedMessages }))
      } else {
        const welcomeMessage: ChatMessage = {
          id: Date.now().toString(),
          sender: 'ai',
          message: getInitialMessage(context),
          timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
        }
        setState(prev => ({ ...prev, messages: [welcomeMessage] }))
      }
    } catch (err) {
      console.warn('Could not load existing conversation:', err)
    }
  }, [context, currentUser, onConversationChange, conversationId])

  // Core reset logic extracted from handleResetChat
  const resetChatCore = useCallback(async () => {
    // Use conversationId prop as authoritative ID (set when loading from list),
    // falling back to state.conversation.id for freshly-created conversations
    const idToDelete = conversationId || state.conversation?.id
    if (idToDelete) {
      try {
        await ConversationService.deleteConversationMessages(idToDelete)
        await ConversationService.updateConversationStatus(idToDelete, 'deleted')
      } catch (err) {
        console.warn('Could not delete conversation from database:', err)
      }
    }

    // Create initial prompt message for the fresh chat
    const initialMessage = getInitialMessage(context)
    const welcomeMessage: ChatMessage = {
      id: Date.now().toString(),
      sender: 'ai',
      message: initialMessage,
      timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    }

    // Reset state with initial message
    setState(prev => ({
      ...prev,
      messages: [welcomeMessage],
      conversation: null,
      newMessage: '',
      error: null,
      processedMessageIds: new Set<string>()
    }))

    if (idToDelete && onConversationDeleted) {
      onConversationDeleted(idToDelete)
    }
    if (onConversationChange) {
      onConversationChange(null)
    }
  }, [conversationId, state.conversation, onConversationChange, onConversationDeleted, context])

  // Expose reset function to parent component (without confirmation since it's handled in parent)
  useImperativeHandle(ref, () => ({
    resetChat: resetChatCore
  }), [resetChatCore])

  // Load existing conversation when context changes
  useEffect(() => {
    loadExistingConversation()
  }, [loadExistingConversation])

  const getConversationTitle = (context: ConversationContext): string => {
    switch (context.type) {
      case 'video':
        return `${context.video.title}`
      case 'generic':
        return '一緒に探究を進めよう！'
      default:
        return 'チャット'
    }
  }

  const handleStopGeneration = () => {
    if (state.abortController) {
      // Abort the ongoing request
      state.abortController.abort()

      // Add system message to chat
      const cancelMessage: ChatMessage = {
        id: Date.now().toString(),
        sender: 'ai',
        message: 'リクエストをキャンセルしました。',
        timestamp: new Date().toLocaleTimeString('ja-JP', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }

      // Clean up state
      setState(prev => ({
        ...prev,
        messages: [...prev.messages, cancelMessage],
        isAiTyping: false,
        isSending: false,
        abortController: null
      }))
    }
  }

  const handleSendMessage = async (messageOverride?: string) => {
    const messageText = messageOverride ?? state.newMessage.trim()
    if (!messageText || !context.chatPrompt || state.isSending) return

    // Create new AbortController for this request
    const abortController = new AbortController()

    setState(prev => ({ ...prev, isSending: true, abortController }))
    const messageId = Date.now().toString()

    // Check if this message ID was already processed
    if (state.processedMessageIds.has(messageId)) {
      setState(prev => ({ ...prev, isSending: false }))
      return
    }

    const newProcessedIds = new Set(state.processedMessageIds)
    newProcessedIds.add(messageId)

    const userMessage: ChatMessage = {
      id: messageId,
      sender: 'user',
      message: messageText,
      timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    }

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      newMessage: '',
      isAiTyping: true,
      processedMessageIds: newProcessedIds
    }))

    try {
      // Check if OpenAI is configured
      if (!OpenAIService.isConfigured()) {
        throw new Error('OpenAI API key is not configured')
      }

      // Create conversation if needed (for logged-in users)
      let currentConversation = state.conversation
      let isFirstMessage = false

      if (currentUser && context.chatPrompt && !currentConversation) {
        try {
          const conversationTitle = context.type === 'generic'
            ? userMessage.message.slice(0, 50)
            : getConversationTitle(context)
          currentConversation = forceNewConversation
            ? await ConversationService.createConversation(currentUser.id, context.chatPrompt.id, conversationTitle)
            : await ConversationService.getOrCreateConversation(currentUser.id, context.chatPrompt.id, conversationTitle)
          setState(prev => ({ ...prev, conversation: currentConversation }))
          if (onConversationChange) {
            onConversationChange(currentConversation)
          }
          isFirstMessage = true
        } catch (convErr) {
          console.warn('Could not create conversation:', convErr)
        }
      }

      // Store initial/welcome message if this is the first message in the conversation
      // The welcome message is already displayed in the UI from loadExistingConversation(),
      // but we need to store it in the database when the conversation is first created
      if (isFirstMessage && currentConversation) {
        // Get the initial message that was already shown to the user
        const initialMessage = getInitialMessage(context)

        // Store initial message in conversation
        try {
          await ConversationService.createMessage(
            currentConversation.id,
            'assistant',
            initialMessage,
            { modelUsed: context.chatPrompt.model || 'gpt-4' }
          )
        } catch (msgErr) {
          console.warn('Could not store initial message:', msgErr)
        }
      }

      // Store user message in database if conversation exists
      if (currentConversation) {
        try {
          await ConversationService.createMessage(
            currentConversation.id,
            'user',
            userMessage.message
          )
        } catch (msgErr) {
          console.warn('Could not store user message:', msgErr)
        }
      }

      // Generate AI response with context
      const conversationHistory = state.messages.map(msg => ({
        sender: msg.sender,
        message: msg.message
      }))

      const aiResponseContent = await OpenAIService.generateResponse(
        userMessage.message,
        {
          video: context.type === 'video' ? context.video : undefined,
          chatPrompt: context.chatPrompt,
          conversationHistory,
          conversationId: currentConversation?.id,
          signal: abortController.signal
        }
      )

      const aiResponseId = (Date.now() + 1).toString()

      // Check if AI response was already processed
      if (!state.processedMessageIds.has(aiResponseId)) {
        const updatedProcessedIds = new Set(state.processedMessageIds)
        updatedProcessedIds.add(aiResponseId)

        const aiResponse: ChatMessage = {
          id: aiResponseId,
          sender: 'ai',
          message: aiResponseContent,
          timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
        }

        setState(prev => ({
          ...prev,
          messages: [...prev.messages, aiResponse],
          processedMessageIds: updatedProcessedIds
        }))
      }

      // Store AI response in database if conversation exists
      if (currentConversation) {
        try {
          const metadata = OpenAIService.getLastResponseMetadata()
          await ConversationService.createMessage(
            currentConversation.id,
            'assistant',
            aiResponseContent,
            {
              tokensUsed: metadata?.usage?.total_tokens,
              modelUsed: metadata?.model || context.chatPrompt?.model || 'gpt-4',
              responseTimeMs: metadata?.responseTimeMs,
              openaiResponseId: metadata?.responseId
            }
          )
        } catch (msgErr) {
          console.warn('Could not store AI response:', msgErr)
        }
      }
    } catch (err) {
      // Handle abort specifically - don't show error message
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('Request was cancelled by user')
        // Don't show error message - already handled by handleStopGeneration
        return
      }

      console.error('Error generating AI response:', err)

      // Fallback to a generic response
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        message: err instanceof Error && err.message.includes('configured')
          ? 'すみません、現在AIチャット機能が利用できません。管理者にお問い合わせください。'
          : 'すみません、応答の生成中にエラーが発生しました。もう一度お試しください。',
        timestamp: new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
      }

      setState(prev => ({ ...prev, messages: [...prev.messages, errorResponse] }))
    } finally {
      setState(prev => ({ ...prev, isAiTyping: false, isSending: false, abortController: null }))
    }
  }

  const handleResetChat = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm('チャットの履歴をすべて削除してもよろしいですか？\nこの操作は取り消せません。')

    if (!confirmed) {
      return
    }

    // Use the core reset logic
    await resetChatCore()
  }

  if (!context.chatPrompt) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${className}`}>
        <div className="p-4 text-center">
          <p className="text-gray-500">チャット機能を読み込み中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col ${height === 'flex-1' ? 'flex-1' : height} ${className}`}>
      <ChatPanel
        messages={state.messages}
        isAiTyping={state.isAiTyping}
        inputValue={state.newMessage}
        onInputChange={(v) => setState(prev => ({ ...prev, newMessage: v }))}
        onSend={() => handleSendMessage()}
        isSending={state.isSending}
        onStop={handleStopGeneration}
      />

      {showResetButton && (
        <div className="border-t p-2 flex justify-center">
          <button
            onClick={handleResetChat}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            title="チャットをリセット"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            チャットをリセット
          </button>
        </div>
      )}
    </div>
  )
})

ChatInterface.displayName = 'ChatInterface'

export default ChatInterface